/* Raw Form footer video.

   The live site ships a 1920x1080 clip with preload="auto", so every page
   pays for it whether or not anyone scrolls that far. Here nothing is
   requested until the footer is nearly in view, and playback stops again once
   it leaves — no decoding on a tab nobody is looking at.

   It is decoration, so anyone who has asked for reduced motion, or whose
   browser reports Data Saver, just gets the static wordmark. */
class RFFooterVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    const src = this.dataset.src;
    if (!this.video || !src) return;

    if (this.optedOut()) {
      this.setAttribute('data-static', '');
      return;
    }

    /* On a phone the band is a still: the clip is 3.6 MB for a strip the
       height of a thumb. The still is sized to the band and, like the clip,
       only fetched once the footer is near. */
    this.stillOnly = !!this.dataset.poster && window.matchMedia('(max-width: 749px)').matches;

    if (!('IntersectionObserver' in window)) {
      this.load();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => (e.isIntersecting ? this.load() : this.pause())),
      { rootMargin: '300px 0px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  optedOut() {
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (motion.matches) return true;
    const net = navigator.connection;
    return !!(net && net.saveData);
  }

  poster() {
    const w = Math.min(1920, Math.ceil((this.clientWidth * (window.devicePixelRatio || 1)) / 200) * 200);
    const url = this.dataset.poster;
    return url + (url.includes('?') ? '&' : '?') + 'width=' + w;
  }

  load() {
    if (this.stillOnly) {
      if (!this.video.poster) {
        this.video.poster = this.poster();
        this.setAttribute('data-still', '');
      }
      return;
    }
    if (!this.loaded) {
      this.loaded = true;
      if (this.dataset.poster) this.video.poster = this.poster();
      this.video.src = this.dataset.src;
      this.video.load();
      this.video.addEventListener('playing', () => this.setAttribute('data-playing', ''), { once: true });
    }
    /* Autoplay can still be refused; the wordmark just stays static then. */
    const p = this.video.play();
    if (p && p.catch) p.catch(() => {});
  }

  pause() {
    if (this.loaded && !this.video.paused) this.video.pause();
  }
}

if (!customElements.get('rf-footer-video')) {
  customElements.define('rf-footer-video', RFFooterVideo);
}
